import { useEffect, useRef, type MutableRefObject } from 'react';
import type { Rnd } from 'react-rnd';
import { resolveSnapBounds, type SnapZone } from '@/shared/lib/resolveSnapBounds';
import { resolveInitialBounds } from './resolveInitialBounds';

interface RestoreOnUnhideArgs {
  hidden: boolean;
  rndRef: MutableRefObject<Rnd | null>;
  maximizedRef: MutableRefObject<boolean>;
  snappedRef: MutableRefObject<boolean>;
  lastSnapZoneRef: MutableRefObject<SnapZone>;
  getParentRect: () => { width: number; height: number } | null;
  getRelativePosition: (el: HTMLElement) => { x: number; y: number };
  minWidth: number;
  minHeight: number;
}

/** 최소화 직전 geometry 를 기억했다가 복원 시 현재 부모 기준으로 다시 맞춘다. */
export function useRestoreOnUnhide({
  hidden,
  rndRef,
  maximizedRef,
  snappedRef,
  lastSnapZoneRef,
  getParentRect,
  getRelativePosition,
  minWidth,
  minHeight,
}: RestoreOnUnhideArgs) {
  // 최소화 직전(아직 visible)의 geometry — display:none 중에는 0×0 으로 측정되므로
  // 복원 시 재clamp 입력으로 이 캡처본을 쓴다.
  const lastVisibleBounds = useRef<{ x: number; y: number; width: number; height: number } | null>(
    null
  );
  const wasHiddenRef = useRef(hidden);

  useEffect(() => {
    if (hidden) {
      wasHiddenRef.current = true;
      const self = rndRef.current?.getSelfElement();
      if (self && self.offsetWidth > 0 && self.offsetHeight > 0) {
        const pos = getRelativePosition(self);
        lastVisibleBounds.current = {
          x: pos.x,
          y: pos.y,
          width: self.offsetWidth,
          height: self.offsetHeight,
        };
      }
      return;
    }
    if (wasHiddenRef.current) {
      // 최소화 동안 부모가 리사이즈됐을 수 있다 — 복원 시 현재 부모 기준으로 재적용.
      wasHiddenRef.current = false;
      const rnd = rndRef.current;
      const rect = getParentRect();
      if (rnd && rect && rect.width > 0 && rect.height > 0) {
        if (maximizedRef.current) {
          rnd.updatePosition({ x: 0, y: 0 });
          rnd.updateSize({ width: rect.width, height: rect.height });
        } else if (snappedRef.current) {
          const bounds = resolveSnapBounds(lastSnapZoneRef.current, rect, {
            width: minWidth,
            height: minHeight,
          });
          if (bounds) {
            rnd.updatePosition({ x: bounds.x, y: bounds.y });
            rnd.updateSize({ width: bounds.width, height: bounds.height });
          }
        } else if (lastVisibleBounds.current) {
          const restored = resolveInitialBounds(lastVisibleBounds.current, rect, {
            width: minWidth,
            height: minHeight,
          });
          rnd.updatePosition({ x: restored.x, y: restored.y });
          rnd.updateSize({ width: restored.width, height: restored.height });
        }
      }
    }
    return undefined;
  }, [
    hidden,
    rndRef,
    maximizedRef,
    snappedRef,
    lastSnapZoneRef,
    getParentRect,
    getRelativePosition,
    minWidth,
    minHeight,
  ]);
}
